import React, { Fragment } from 'react';
import styled from '@emotion/styled';
import { themeGet } from 'styled-system';
import { Box, Container, Flex, Hide } from 'roo-ui/components';
import SearchResultFetcher from 'components/SearchResultFetcher';
import PromoAreaDealsFetcher from 'components/PromoAreaDealsFetcher';
import CampaignMessaging from 'components/CampaignMessaging';
import MapButton from './Sidebar/MapButton';
import PayWithToggleMessage from 'components/PayWithToggleMessage';
import SkipToContentButton from 'components/SkipToContentButton';
import TreatmentToggle from 'components/TreatmentToggle';
import WelcomeMessage from 'components/WelcomeMessage';
import { PAY_WITH_OPTIONS } from 'lib/enums/payment';
import SearchControls from './SearchControls';
import { SearchResultSummaryConnected } from './SearchResultSummary';
import SortSelect from './SortSelect';
import SidebarFilters from './Sidebar/Filters';
import SidebarContainer from './Sidebar/SidebarContainer';
import SearchList from './SearchList';
import PayWith from './PayWith';
import SearchError from './SearchError';
import DynamicMessageBox from './Messaging/DynamicMessageBox';
import ListSearchMeta from './ListSearchMeta';
import PhoneShortcutFilters from './PhoneShortcutFilters';
import PromoArea from './PromoArea';
import LegacyBrowserBoundary from 'components/LegacyBrowserBoundary';

const SkipToResultsButton = styled(SkipToContentButton)`
  z-index: ${themeGet('zIndices.dropdownContent')};
  padding: ${themeGet('space.1')};
  left: ${themeGet('space.2')};
  top: ${themeGet('space.2')};
`;

const CollapsibleBox = styled(Box)`
  flex-shrink: 0;
`;

const MainContent = styled(Box)`
  flex: 1;
  position: relative;
`;

const DisplaySidebarFilters = styled(Box)`
  position: absolute;
  top: 60px;
  left: 0;
  z-index: ${themeGet('zIndices.modalContent')};
  display: ${(props) => (props.isSidebarOpen ? 'block' : 'none')};
  width: ${themeGet('uiStructure.mediumScreenSidebar')};
`;

DisplaySidebarFilters.displayName = 'DisplaySidebarFilters';

MainContent.displayName = 'MainContent';

const ListSearchLayout = React.memo(() => {
  return (
    <Fragment>
      <SearchResultFetcher />
      <LegacyBrowserBoundary>
        <PromoAreaDealsFetcher />
      </LegacyBrowserBoundary>
      <ListSearchMeta />
      <WelcomeMessage />
      <SearchControls />
      <CampaignMessaging />
      <LegacyBrowserBoundary>
        <TreatmentToggle split="deals_promo_area" treatment="on">
          <Hide xs sm data-testid="promo-area-media-wrapper">
            <PromoArea />
          </Hide>
        </TreatmentToggle>
      </LegacyBrowserBoundary>
      <Container>
        <DynamicMessageBox />
      </Container>
      <div id="main-content"></div>
      <PhoneShortcutFilters />
      <Container display={['none', 'block']}>
        <SearchResultSummaryConnected data-testid="search-result-summary-desktop" />
      </Container>
      <Container>
        <Flex mt={[0, 4, 4]} flex="1 1 auto">
          <CollapsibleBox flexBasis={[0, 0, 400]}>
            <Box display={['none', 'none', 'block']}>
              <SidebarContainer position="relative">
                <SkipToResultsButton as="a" href="#search-results" color="black" bg="white">
                  Skip to content
                </SkipToResultsButton>
                <MapButton />
                <SidebarFilters />
              </SidebarContainer>
            </Box>
          </CollapsibleBox>
          <div id="search-results"></div>
          <MainContent>
            <Box display={['none', 'block']}>
              <Flex mb={10} alignItems="center">
                <Box display={['none', 'block']}>
                  <SortSelect />
                </Box>
                <Flex ml="auto" position="relative">
                  <PayWithToggleMessage pointingDirection="bottom" position="absolute" pointerPosition="75%" bottom="120%" right="0px" />
                  <PayWith name="payWith" options={PAY_WITH_OPTIONS} />
                </Flex>
              </Flex>
            </Box>
            <SearchError />
            <SearchList />
          </MainContent>
        </Flex>
      </Container>
    </Fragment>
  );
});

ListSearchLayout.displayName = 'ListSearchLayout';

export default ListSearchLayout;
